import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI } from '../services/api';
import Navbar from '../components/HomePage/Navbar';
import Footer from '../components/HomePage/Footer';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Image, ArrowRight } from 'lucide-react';

const BlogCard = ({ blog, index }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), index * 100);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <Link
      to={`/blog/${blog.slug}`}
      className={`group block bg-ag-black-700 rounded-xl overflow-hidden border border-ag-black-600 hover:border-ag-green/50 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-ag-green/10 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Thumbnail */}
      <div className="aspect-[16/9] bg-ag-black-800 overflow-hidden">
        {blog.thumbnail?.url ? (
          <img
            src={blog.thumbnail.url}
            alt={blog.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-12 h-12 text-ag-black-400" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-ag-green">
            {blog.subject}
          </span>
          <span className="text-xs text-ag-black-300">
            {new Date(blog.publishedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>

        <h3 className="text-white font-bold text-base sm:text-lg leading-snug line-clamp-2 group-hover:text-ag-green transition-colors duration-300">
          {blog.title}
        </h3>

        {blog.excerpt && (
          <p className="text-ag-black-300 text-sm line-clamp-2 leading-relaxed">
            {blog.excerpt}
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          {blog.author && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-ag-black-600 overflow-hidden">
                {blog.author.photo && blog.author.photo !== 'default.jpg' ? (
                  <img
                    src={blog.author.photo}
                    alt={blog.author.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-ag-black-300 font-bold">
                    {blog.author.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
              <span className="text-xs text-ag-black-300">{blog.author.name}</span>
            </div>
          )}
          <span className="text-ag-green text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Read <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const res = await blogAPI.getPublished({ page, limit: 12 });
        setBlogs(res.data.data.blogs);
        setTotalPages(res.data.totalPages);
      } catch (err) {
        console.error('Failed to fetch blogs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [page]);

  return (
    <div className="w-full bg-black overflow-x-hidden min-h-screen">
      {/* Navbar */}
      <div className="relative z-10">
        <Navbar />
      </div>

      {/* Hero Section */}
      <section className="pt-8 sm:pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20">
        <div
          ref={titleRef}
          className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8 text-center"
        >
          <h1
            className={`font-sourceSans font-black tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Our <span className="text-ag-green">Blog</span>
          </h1>
          <p
            className={`text-ag-black-300 mt-4 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            Tips, tutorials, and updates from the Autograph team
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-16 sm:pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-20">
              <Image className="w-16 h-16 text-ag-black-400 mx-auto mb-4" />
              <p className="text-ag-black-300 text-xl font-medium">No posts yet</p>
              <p className="text-ag-black-400 text-sm mt-2">
                Check back soon for new content!
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((blog, index) => (
                  <BlogCard key={blog._id} blog={blog} index={index} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-12">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-5 py-2 rounded-lg text-sm font-medium bg-ag-black-700 text-white border border-ag-black-400 hover:border-ag-green disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-ag-black-300 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-5 py-2 rounded-lg text-sm font-medium bg-ag-black-700 text-white border border-ag-black-400 hover:border-ag-green disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogsPage;
